import { useState } from 'react';
import { Plus, Bell, X } from 'lucide-react';
import DataTable from '@/components/DataTable';
import type { AlertRule } from '@/types';

const MOCK_ALERTS: AlertRule[] = [
  {
    id: '1',
    name: 'High Attrition Warning',
    metric: 'Attrition Rate',
    operator: '>',
    threshold: 15,
    enabled: true,
    channels: ['email', 'slack'],
    lastTriggered: '2026-03-10',
  },
  {
    id: '2',
    name: 'Headcount Drop',
    metric: 'Total Headcount',
    operator: '<',
    threshold: 1200,
    enabled: true,
    channels: ['email'],
    lastTriggered: undefined,
  },
  {
    id: '3',
    name: 'Low Diversity Index',
    metric: 'Diversity Index',
    operator: '<',
    threshold: 0.6,
    enabled: false,
    channels: ['email', 'slack'],
    lastTriggered: '2026-02-15',
  },
  {
    id: '4',
    name: 'Tenure Alert',
    metric: 'Avg Tenure',
    operator: '<',
    threshold: 2.0,
    enabled: true,
    channels: ['slack'],
    lastTriggered: undefined,
  },
];

const ALERT_HISTORY = [
  { id: '1', rule: 'High Attrition Warning', metric: 'Attrition Rate', value: '16.2%', triggeredAt: '2026-03-10 09:15', status: 'resolved' },
  { id: '2', rule: 'Low Diversity Index', metric: 'Diversity Index', value: '0.58', triggeredAt: '2026-02-15 14:30', status: 'resolved' },
  { id: '3', rule: 'High Attrition Warning', metric: 'Attrition Rate', value: '15.8%', triggeredAt: '2026-01-22 11:00', status: 'resolved' },
];

const historyColumns = [
  { key: 'rule', header: 'Alert Rule' },
  { key: 'metric', header: 'Metric' },
  { key: 'value', header: 'Triggered Value' },
  { key: 'triggeredAt', header: 'Triggered At' },
  {
    key: 'status',
    header: 'Status',
    render: (item: Record<string, unknown>) => (
      <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        {(item.status as string).charAt(0).toUpperCase() + (item.status as string).slice(1)}
      </span>
    ),
  },
];

const METRICS_LIST = ['Attrition Rate', 'Total Headcount', 'Diversity Index', 'Avg Tenure', 'Avg Compensation'];
const OPERATORS = ['>', '<', '>=', '<=', '=='];
const CHANNELS = ['email', 'slack'];

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<AlertRule[]>(MOCK_ALERTS);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formMetric, setFormMetric] = useState(METRICS_LIST[0]);
  const [formOperator, setFormOperator] = useState('>');
  const [formThreshold, setFormThreshold] = useState('');
  const [formChannels, setFormChannels] = useState<string[]>(['email']);

  const toggleAlert = (id: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, enabled: !a.enabled } : a))
    );
  };

  const handleCreateAlert = () => {
    if (!formName || !formThreshold) return;

    const newAlert: AlertRule = {
      id: String(Date.now()),
      name: formName,
      metric: formMetric,
      operator: formOperator as AlertRule['operator'],
      threshold: parseFloat(formThreshold),
      enabled: true,
      channels: formChannels,
    };

    setAlerts((prev) => [...prev, newAlert]);
    setShowForm(false);
    setFormName('');
    setFormThreshold('');
    setFormChannels(['email']);
  };

  const toggleChannel = (channel: string) => {
    setFormChannels((prev) =>
      prev.includes(channel)
        ? prev.filter((c) => c !== channel)
        : [...prev, channel]
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
          <p className="mt-1 text-sm text-gray-500">
            Configure threshold alerts for key workforce metrics
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setShowForm(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Alert
        </button>
      </div>

      {/* Create Alert Form */}
      {showForm && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900">
              New Alert Rule
            </h3>
            <button
              onClick={() => setShowForm(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Alert Name
              </label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="input-field"
                placeholder="e.g. High Attrition Alert"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Metric
              </label>
              <select
                value={formMetric}
                onChange={(e) => setFormMetric(e.target.value)}
                className="input-field"
              >
                {METRICS_LIST.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Operator
                </label>
                <select
                  value={formOperator}
                  onChange={(e) => setFormOperator(e.target.value)}
                  className="input-field"
                >
                  {OPERATORS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Threshold
                </label>
                <input
                  type="number"
                  value={formThreshold}
                  onChange={(e) => setFormThreshold(e.target.value)}
                  className="input-field"
                  placeholder="15"
                />
              </div>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notification Channels
            </label>
            <div className="flex gap-3">
              {CHANNELS.map((ch) => (
                <label
                  key={ch}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formChannels.includes(ch)}
                    onChange={() => toggleChannel(ch)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 capitalize">{ch}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button className="btn-primary" onClick={handleCreateAlert}>
              Create Alert
            </button>
          </div>
        </div>
      )}

      {/* Alert Rules */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Active Rules</h2>
        {alerts.map((alert) => (
          <div
            key={alert.id}
            className="card flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div
                className={`rounded-lg p-2 ${
                  alert.enabled
                    ? 'bg-primary-50 text-primary-600'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                <Bell className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium text-gray-900">{alert.name}</p>
                <p className="text-sm text-gray-500">
                  {alert.metric} {alert.operator} {alert.threshold} -- via{' '}
                  {alert.channels.join(', ')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {alert.lastTriggered && (
                <span className="text-xs text-gray-400">
                  Last triggered: {alert.lastTriggered}
                </span>
              )}
              <button
                onClick={() => toggleAlert(alert.id)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  alert.enabled ? 'bg-primary-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                    alert.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Alert History */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          Alert History
        </h2>
        <DataTable columns={historyColumns} data={ALERT_HISTORY} pageSize={10} />
      </div>
    </div>
  );
}
